/**
 * SampleCardsSection
 *
 * A curated gallery of real card front designs on the landing page, so visitors
 * can actually see what arrives in the mailbox before they sign up. Renders the
 * same procedural CardDesignPreview used in the app and on /designs, so these are
 * the genuine designs — not mockups.
 *
 * The eight designs below are a hand-picked cross-section spanning occasions
 * (birthday, anniversary, Christmas, Valentine's, Mother's & Father's Day,
 * graduation, thinking of you) and styles (modern, elegant, floral, sentimental).
 * They are hardcoded so the section prerenders to static HTML without the runtime
 * /api/designs fetch. Keep these IDs in sync with backend/src/data/cardDesigns.ts.
 */

import { Link } from 'react-router-dom';
import CardDesignPreview, { type DesignData } from './cards/CardDesignPreview';

const SAMPLE_DESIGNS: DesignData[] = [
  {
    id: 'bday-confetti',
    name: 'Celebration',
    heading: 'Happy Birthday',
    tagline: 'Bold, joyful, impossible to ignore',
    style: 'modern',
    bg_color: '#1B2A4A',
    bg_type: 'solid',
    pattern: 'confetti',
    pattern_color: 'rgba(201,168,76,0.35)',
    accent_color: '#C9A84C',
    text_color: '#FFFFFF',
    emoji: '',
  },
  {
    id: 'anni-gold',
    name: 'Golden Year',
    heading: 'Happy Anniversary',
    tagline: 'Luxe gold for a milestone worth celebrating',
    style: 'elegant',
    bg_color: 'linear-gradient(135deg, #1B2A4A 0%, #2C3E68 100%)',
    bg_type: 'gradient',
    pattern: 'none',
    pattern_color: 'transparent',
    accent_color: '#C9A84C',
    text_color: '#FFFFFF',
    emoji: '',
  },
  {
    id: 'val-roses',
    name: 'Red Roses',
    heading: "Happy Valentine's Day",
    tagline: 'The classic for a reason',
    style: 'floral',
    bg_color: '#FFF0F0',
    bg_type: 'solid',
    pattern: 'botanical',
    pattern_color: 'rgba(192,83,74,0.20)',
    accent_color: '#C0534A',
    text_color: '#1B2A4A',
    emoji: '',
  },
  {
    id: 'mom-blush',
    name: 'Blush Garden',
    heading: "Happy Mother's Day",
    tagline: 'Soft florals for the woman who does everything',
    style: 'floral',
    bg_color: '#FDF5F7',
    bg_type: 'solid',
    pattern: 'botanical',
    pattern_color: 'rgba(232,165,152,0.30)',
    accent_color: '#E8A598',
    text_color: '#1B2A4A',
    emoji: '',
  },
  {
    id: 'dad-classic',
    name: "Dad's Day",
    heading: "Happy Father's Day",
    tagline: 'Strong and warm, just like him',
    style: 'elegant',
    bg_color: '#1B2A4A',
    bg_type: 'solid',
    pattern: 'grid',
    pattern_color: 'rgba(255,255,255,0.05)',
    accent_color: '#C9A84C',
    text_color: '#FFFFFF',
    emoji: '',
  },
  {
    id: 'xmas-classic',
    name: 'Classic Christmas',
    heading: 'Merry Christmas',
    tagline: 'Timeless holiday warmth in red and green',
    style: 'elegant',
    bg_color: '#1A3A1A',
    bg_type: 'solid',
    pattern: 'botanical',
    pattern_color: 'rgba(201,168,76,0.20)',
    accent_color: '#C9A84C',
    text_color: '#FFFFFF',
    emoji: '',
  },
  {
    id: 'grad-gold',
    name: 'Achievement Unlocked',
    heading: 'Congratulations',
    tagline: 'Gold for the hardest-earned milestone',
    style: 'modern',
    bg_color: 'linear-gradient(135deg, #1B2A4A 0%, #2A3F6A 100%)',
    bg_type: 'gradient',
    pattern: 'none',
    pattern_color: 'transparent',
    accent_color: '#C9A84C',
    text_color: '#FFFFFF',
    emoji: '',
  },
  {
    id: 'tou-sage',
    name: 'Just Because',
    heading: 'Thinking of You',
    tagline: 'Simple, genuine, from the heart',
    style: 'sentimental',
    bg_color: '#F0F7F1',
    bg_type: 'solid',
    pattern: 'botanical',
    pattern_color: 'rgba(122,158,126,0.20)',
    accent_color: '#7A9E7E',
    text_color: '#1B2A4A',
    emoji: '',
  },
];

export default function SampleCardsSection() {
  return (
    <section id="sample-cards" className="py-24 bg-white">
      <div className="section-container">

        {/* Heading */}
        <div className="text-center mb-14">
          <p className="font-body text-xs font-semibold tracking-widest text-sage uppercase mb-3">
            The cards
          </p>
          <h2 className="section-heading text-4xl sm:text-5xl">
            A card for every kind of moment
          </h2>
          <p className="mt-4 font-body text-gray-500 text-base max-w-xl mx-auto">
            Every card is professionally printed on premium stock and mailed first class.
            Here is a small taste. There are dozens more, one for every person and every
            occasion in your life.
          </p>
        </div>

        {/* Gallery */}
        <div className="flex flex-wrap justify-center gap-6 max-w-5xl mx-auto">
          {SAMPLE_DESIGNS.map((design) => (
            <CardDesignPreview key={design.id} design={design} size="lg" />
          ))}
        </div>

        {/* Browse-all link */}
        <div className="text-center mt-12">
          <Link
            to="/designs"
            className="inline-block font-body text-sm font-semibold text-navy border border-navy rounded-full px-7 py-3 transition-all hover:bg-navy hover:text-white"
          >
            Browse every design →
          </Link>
        </div>

      </div>
    </section>
  );
}
